import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { getVendorInfo, normalizeWikimediaThumbUrl } from '../../utils/vendorLogos';
import { useBrandRegistry } from '../../hooks/useBrandRegistry';
import { brandMark } from './connectorHealth';
import { ingest } from './ingestionTokens';

interface BrandMarkTileProps {
  brand: string;
  size?: number;
}

/**
 * 44px bordered tile from the handoff. It carries the catalog logo when the
 * registry has one and falls back to the short text mark when it does not.
 */
export const BrandMarkTile: React.FC<BrandMarkTileProps> = ({ brand, size = 44 }) => {
  const { data: registry } = useBrandRegistry();
  const [imageFailed, setImageFailed] = useState(false);

  const logoUrl = useMemo(() => {
    const vendorInfo = getVendorInfo(brand, true);
    const normalized = String(brand || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const registryBrand = (registry || []).find(
      (item) => item.slug === normalized || String(item.name || '').toLowerCase() === String(brand || '').toLowerCase()
    );
    return normalizeWikimediaThumbUrl(vendorInfo.logo || registryBrand?.logoUrl || '');
  }, [brand, registry]);

  // Without this, one brand whose logo 404s leaves every brand selected after it
  // showing initials, because the tile stays mounted across selections.
  useEffect(() => {
    setImageFailed(false);
  }, [logoUrl]);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        border: `1px solid ${ingest.color.borderInner}`,
        borderRadius: '8px',
        background: ingest.color.sunken,
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
        fontFamily: ingest.font.sans,
        fontSize: '11px',
        fontWeight: 700,
        color: ingest.color.dim,
      }}
    >
      {logoUrl && !imageFailed ? (
        <Box
          component="img"
          src={logoUrl}
          alt={`${brand} logo`}
          onError={() => setImageFailed(true)}
          sx={{ width: '78%', height: '78%', objectFit: 'contain' }}
        />
      ) : (
        brandMark(brand)
      )}
    </Box>
  );
};

export default BrandMarkTile;
